import {
  getDayjsObject,
  getOptionsFromRefList,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import CommonDatePicker from "@/common/elements/DatePicker";
import Select from "@/common/elements/InputElements/Select";
import DynamicForm from "@/common/DynamicForm";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import { setBookings } from "@/slices/bookingsSlice";
import { debounce } from "lodash";
import dayjs from "dayjs";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import { SCHEMA_NAME } from "@/util/common_variable";

// VisitorLogExtra's "Visitor_Relationship_U" column already has its own
// dedicated Select control in InmateToVisit.jsx (relationshipOptions,
// getRelationshipIdFromLabel) — this section renders every other live "_U"
// column generically, so that one isn't duplicated here.
const RELATIONSHIP_COLUMN_NAME = "visitorRelationship";

const VisitorInformation = ({
  visitorInformationRef,
  formData,
  updateFormData,
  startingPoint = "",
}) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector((s) => s?.bookings);
  const [form] = Form.useForm();
  const [visitorInformation, setVisitorInformation] = useState(null);

  const isCancelled = bookings?.cancelled === true;

  // -----------------------------------------------------------------------
  // VisitorLogExtra — PCI's per-client dynamic "_U" columns for this
  // visitor log entry (VisitorLogInterface.asp's "Additional Visitor Log
  // Information" section). Fetched/rendered/saved the same generic way as
  // every other extra table — see util/extraColumnActions.js.
  // -----------------------------------------------------------------------
  const [visitorLogExtraFields, setVisitorLogExtraFields] = useState([]);
  const [visitorLogExtraFieldsReady, setVisitorLogExtraFieldsReady] = useState(false);
  const visitorLogExtraTypeMapRef = useRef({});

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/VisitorLogExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
        const converted = convertApiFields(tableData, "VisitorLogExtra");
        const typeMap = {};
        converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
        visitorLogExtraTypeMapRef.current = typeMap;
        setVisitorLogExtraFields(converted);
        setVisitorLogExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error("[VisitorInformation] VisitorLogExtra field defs fetch failed:", error);
      });
  }, []);

  // Fetches the saved extra row once a real visitorLogId is present (edit
  // mode) — a brand new visitor log entry has no id yet, so there's
  // nothing to fetch until it's saved once. Dispatched straight into
  // bookings.visitorLogExtra[0] — the same place InmateToVisit.jsx's
  // relationship dropdown already reads/writes.
  useEffect(() => {
    if (!visitorLogExtraFieldsReady) return;
    const visitorLogId = bookings?.visitorLogId;
    if (!isNonEmptyVariable(visitorLogId)) return;

    const currentExtraId = bookings?.visitorLogExtra?.[0]?.visitorLogExtraId;
    if (currentExtraId) return; // already have the real, saved row's PK

    fetchExtraTableRecord("VisitorLogExtra", "visitorLogID", visitorLogId)
      .then((result) => {
        if (!result) return;
        const existing = bookings?.visitorLogExtra?.[0] || {};
        dispatch(
          setBookings({
            visitorLogExtra: [
              {
                ...result.values,
                ...existing, // preserve any edits typed before this resolved
                visitorLogExtraId:
                  result.raw?.VisitorLogExtraID ?? existing?.visitorLogExtraId ?? 0,
                visitorLogId,
              },
            ],
          }),
        );
      })
      .catch((error) => {
        console.error("[VisitorInformation] VisitorLogExtra fetch failed:", error);
      });
  }, [bookings?.visitorLogId, visitorLogExtraFieldsReady]);

  // -----------------------------------------------------------------------
  // Sync formData prop → local state
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (formData) setVisitorInformation(formData);
  }, [formData]);

  // -----------------------------------------------------------------------
  // Sync bookings → local state (with dayjs conversions)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (bookings !== null) {
      const extra = bookings?.visitorLogExtra?.[0] || {};
      const extraWithDates = Object.keys(extra).reduce((acc, key) => {
        const val = extra[key];
        const htmlType = visitorLogExtraTypeMapRef.current[key];
        const isDateField = htmlType === "date" || htmlType === "datetime";
        acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
        return acc;
      }, {});

      setVisitorInformation({
        reasonForVisit:      bookings?.reasonForVisit,
        location:            isNonEmptyVariable(bookings?.location) ? parseInt(bookings.location) : null,
        scheduledDateTime:   getDayjsObject(bookings?.scheduledDate,    bookings?.scheduledTime),
        arrivalDateTime:     getDayjsObject(bookings?.arrivalDate,      bookings?.arrivalTime),
        departureDateTime:   getDayjsObject(bookings?.departureDate,    bookings?.departureTime),
        cancellationDateTime: getDayjsObject(bookings?.cancellationDate, bookings?.cancellationTime),
        cancellationReason:  bookings?.cancellationReason ? parseInt(bookings.cancellationReason) : null,
        ...extraWithDates,
      });
    } else {
      setVisitorInformation(null);
    }
  }, [bookings]);

  // Sync local state → antd form
  useEffect(() => {
    visitorInformation !== null
      ? form.setFieldsValue(visitorInformation)
      : form.resetFields();
  }, [visitorInformation]);

  // -----------------------------------------------------------------------
  // Debounced form → Redux sync
  // -----------------------------------------------------------------------
  const dataModify = debounce(() => {
    const values = form.getFieldsValue();

    // Split VisitorLogExtra's dynamic fields out of `values` — they're
    // dispatched into bookings.visitorLogExtra[0] below, not spread flat
    // onto bookings the way the rest of this form's fields are.
    const visitorLogExtraFieldKeys = visitorLogExtraFields.map((f) => f.columnName);
    const visitorLogExtraValues = {};
    visitorLogExtraFieldKeys.forEach((k) => {
      if (k in values) {
        const val = values[k];
        const htmlType = visitorLogExtraTypeMapRef.current[k];
        visitorLogExtraValues[k] =
          val && (htmlType === "date" || htmlType === "datetime")
            ? dayjs(val).format(htmlType === "datetime" ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY")
            : (val ?? null);
        delete values[k];
      }
    });

    values.scheduledDateTime   = values?.scheduledDateTime   ? dayjs(values.scheduledDateTime).format("MM/DD/YYYY HH:mm")   : null;
    values.arrivalDateTime     = values?.arrivalDateTime     ? dayjs(values.arrivalDateTime).format("MM/DD/YYYY HH:mm")     : null;
    values.departureDateTime   = values?.departureDateTime   ? dayjs(values.departureDateTime).format("MM/DD/YYYY HH:mm")   : null;
    values.cancellationDateTime = values?.cancellationDateTime ? dayjs(values.cancellationDateTime).format("MM/DD/YYYY HH:mm") : null;

    const data = {
      ...values,
      scheduledDate:    values?.scheduledDateTime?.split(" ")[0],
      scheduledTime:    values?.scheduledDateTime?.split(" ")[1],
      arrivalDate:      values?.arrivalDateTime?.split(" ")[0],
      arrivalTime:      values?.arrivalDateTime?.split(" ")[1],
      departureDate:    values?.departureDateTime?.split(" ")[0],
      departureTime:    values?.departureDateTime?.split(" ")[1],
      cancellationDate: values?.cancellationDateTime?.split(" ")[0],
      cancellationTime: values?.cancellationDateTime?.split(" ")[1],
    };

    delete data.scheduledDateTime;
    delete data.arrivalDateTime;
    delete data.departureDateTime;

    if (visitorLogExtraFieldKeys.length > 0) {
      const existingExtra = bookings?.visitorLogExtra?.[0] || {};
      data.visitorLogExtra = [
        {
          ...existingExtra,
          ...visitorLogExtraValues,
          visitorLogExtraId: existingExtra?.visitorLogExtraId ?? 0,
          visitorLogId: existingExtra?.visitorLogId ?? bookings?.visitorLogId ?? 0,
        },
      ];
    }

    dispatch(setBookings({ ...bookings, ...data }));
  }, 600);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <FieldSecurityProvider
      entity={SCHEMA_NAME.VISITOR_LOG}
      startingPoint={startingPoint}
      bookingsFieldsEnabled={bookingsFieldsEnabled}
    >
      <Form
        form={form}
        initialValues={visitorInformation}
        ref={visitorInformationRef}
        onFieldsChange={dataModify}
      >
        <StyledGridRow gutter={16}>

          {/* Reason For Visit */}
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="reasonForVisit"
              formItemProps={{
                name: "reasonForVisit", label: "Reason For Visit",
                rules: [{ required: true, message: "You must enter the reason for visit" }],
              }}
            >
              <Select options={getOptionsFromRefList("Reason_For_Visit")} />
            </SecureFormItem>
          </StyledGridCol>

          {/* Location */}
          <StyledGridCol lg={6}>
            <SecureFormItem
              fieldName="location"
              formItemProps={{ name: "location", label: "Location" }}
            >
              <Select options={getOptionsFromRefList("Loan_Destination", false, false, true)} />
            </SecureFormItem>
          </StyledGridCol>

          <StyledGridCol lg={12} />

          {/* Scheduled Date/Time */}
          <StyledGridCol lg={8}>
            <SecureFormItem
              fieldName="scheduledDateTime"
              formItemProps={{ name: "scheduledDateTime", label: "Scheduled Date/Time" }}
            >
              <CommonDatePicker placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" />
            </SecureFormItem>
          </StyledGridCol>

          {/* Arrival & Departure — only when NOT cancelled */}
          {!isCancelled && (
            <>
              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="arrivalDateTime"
                  formItemProps={{ name: "arrivalDateTime", label: "Arrival Date/Time" }}
                >
                  <CommonDatePicker placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" />
                </SecureFormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="departureDateTime"
                  formItemProps={{ name: "departureDateTime", label: "Departure Date/Time" }}
                >
                  <CommonDatePicker placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" />
                </SecureFormItem>
              </StyledGridCol>
            </>
          )}

          {/* Cancellation fields — only when cancelled */}
          {isCancelled && (
            <>
              <StyledGridCol lg={6}>
                <SecureFormItem
                  fieldName="cancellationReason"
                  formItemProps={{
                    name: "cancellationReason", label: "Cancellation Reason",
                    rules: [{ required: true, message: "You must select the reason for cancellation" }],
                  }}
                >
                  <Select options={getOptionsFromRefList("Cancellation_Reason")} />
                </SecureFormItem>
              </StyledGridCol>

              {/* cancellationDateTime — always read-only (disabled={true}), overrideDisabled */}
              <StyledGridCol lg={8}>
                <SecureFormItem
                  fieldName="cancellationDateTime"
                  overrideDisabled
                  formItemProps={{
                    name: "cancellationDateTime", label: "Cancellation Date/Time",
                    rules: [{ required: true, message: "You must enter cancellation date & time" }],
                  }}
                >
                  <CommonDatePicker placeholder="MM/DD/YYYY HH:mm" format="MM/DD/YYYY HH:mm" />
                </SecureFormItem>
              </StyledGridCol>
            </>
          )}

        </StyledGridRow>

        {visitorLogExtraFields.filter((sf) => sf.columnName !== RELATIONSHIP_COLUMN_NAME).length > 0 && (
          <>
            <StyledSectionHeading marginTop="20px">
              <span>Additional Visitor Log Information</span>
            </StyledSectionHeading>
            <StyledGridRow gutter={16}>
              {visitorLogExtraFields
                .filter((sf) => sf.columnName !== RELATIONSHIP_COLUMN_NAME)
                .map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={form}
                    bookingsFieldsEnabled={bookingsFieldsEnabled}
                  />
                ))}
            </StyledGridRow>
          </>
        )}
      </Form>
    </FieldSecurityProvider>
  );
};

export default VisitorInformation;